import { useCallback, useEffect, useState } from "react";
import { getCultures, getCultureReferentiel } from "@/lib/api";

/**
 * Cultures de l'appli. La liste vient du référentiel des cultures (écran admin « Cultures ») ;
 * cette liste de secours ne sert que si le serveur ne répond pas.
 */
export const FALLBACK_CULTURES = [
  "Agrumes", "Ananas", "Banane", "Café", "Canne à sucre", "Fraise", "Fruit de la passion",
  "Géranium rosat", "Goyavier", "Letchi", "Mangue", "Maraîchage", "Palmiste", "Pomme de terre",
  "Prairie permanente", "Vanille", "Verger",
];

export const AUTRE = "__autre__";

export const normCulture = (s) => (s || "").normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase().replace(/\s+/g, " ").trim();

export const regionOfParcel = (parcelId) => ((parcelId || "").startsWith("YT-") ? "mayotte" : "reunion");

const cache = {};

/** Libellés des cultures de la région (référentiel), avec la liste de secours en attendant. */
export function useCultures(region, { saisie = false } = {}) {
  const key = `${region || "all"}|${saisie}`;
  const [list, setList] = useState(cache[key] || FALLBACK_CULTURES);
  useEffect(() => {
    let alive = true;
    if (cache[key]) { setList(cache[key]); return undefined; }
    getCultures(region, saisie)
      .then((r) => {
        if (Array.isArray(r.data) && r.data.length) { cache[key] = r.data; if (alive) setList(r.data); }
      })
      .catch(() => {});
    return () => { alive = false; };
  }, [key, region, saisie]);
  return list;
}

let refPromise = null;

/** Référentiel des cultures, indexé par libellé et synonymes (chargé une fois). */
function loadReferentiel() {
  refPromise = refPromise || getCultureReferentiel().then((r) => {
    const byName = {};
    for (const c of r.data || []) for (const n of [c.libelle, ...(c.synonymes || [])]) byName[normCulture(n)] = c;
    return byName;
  }).catch(() => { refPromise = null; return {}; });
  return refPromise;
}

function useReferentielIndex() {
  const [byName, setByName] = useState({});
  useEffect(() => {
    let alive = true;
    loadReferentiel().then((m) => { if (alive) setByName(m); });
    return () => { alive = false; };
  }, []);
  return byName;
}

/** Couleur d'une culture (libellé ou synonyme), d'après le référentiel. */
export function useCultureColors(fallback = "#2D6A4F") {
  const byName = useReferentielIndex();
  return (name) => byName[normCulture(name)]?.couleur_carte || fallback;
}

/** Culture du référentiel désignée par un texte (libellé ou synonyme), ou null. */
export function useCultureLookup() {
  const byName = useReferentielIndex();
  return useCallback((name) => byName[normCulture(name)] || null, [byName]);
}
